import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAgents } from "../../hooks/useAgents";
import { useUsers } from "../../hooks/useUsers";
import AssigneeTransferField from "../AssigneeTransferField";
import LoggedUsersFallbackField from "../LoggedUsersFallbackField";
import {
  handoffTargetToSelection,
  selectionToHandoffTarget,
} from "../../utils/passagemRecoveryHandoff";
import { selectionRequiresLoggedUsersOnlyDisabled } from "../../utils/assigneeTransferField";
import { applyLoggedUsersOnlyFlag } from "../../utils/loggedUsersFallback";
import LeadTypeCascadeSelect from "../LeadTypeCascadeSelect";
import {
  complaintsDefaultsPatchFromLeadTypes,
  leadTypesFromComplaintsDefaults,
} from "../../utils/leadTypeCascade";
import { translateSkillForm } from "../../utils/skillLifecycle";

export default function ComplaintsSuggestionsSkillConfigSection({
  skillConfig = {},
  mergeComplaintsConfig,
  readOnly = false,
  currentAgentId,
}) {
  const { t } = useTranslation("settings");
  const { data: agentsQuery, isLoading: loadingAgents } = useAgents(1, 100, "");
  const { data: usersData, isLoading: loadingUsers } = useUsers(1, 200, "", "todos");
  const agents = agentsQuery?.agents || [];
  const users = usersData?.users ?? [];

  const queueLabel = t("agentsListPage.lifecycleTransferQueue", {
    defaultValue: "Fila de atendimento humano",
  });

  const handoffAgents = useMemo(
    () =>
      (Array.isArray(agents) ? agents : []).filter(
        (a) =>
          String(a.id) !== String(currentAgentId || "") &&
          (a.status === "active" || a.status === true)
      ),
    [agents, currentAgentId]
  );

  const handoffSelection = useMemo(
    () =>
      handoffTargetToSelection(skillConfig.handoff_target || {}, {
        users,
        agents: handoffAgents,
        labels: { queue: queueLabel },
      }),
    [skillConfig.handoff_target, users, handoffAgents, queueLabel]
  );

  const disableLoggedUsersOnly = selectionRequiresLoggedUsersOnlyDisabled(handoffSelection);
  const loggedUsersOnly = skillConfig.handoff_target?.logged_users_only === true;

  const formCopy = (suffix, fallbackKey) =>
    translateSkillForm(t, "complaints_suggestions", suffix, fallbackKey);

  const applyHandoffSelection = (selection) => {
    const ht = skillConfig.handoff_target || {};
    const nextDisable = selectionRequiresLoggedUsersOnlyDisabled(selection);
    mergeComplaintsConfig({
      handoff_target: selectionToHandoffTarget(selection, {
        logged_users_only: nextDisable ? false : ht.logged_users_only === true,
        logged_users_fallback: nextDisable ? null : ht.logged_users_fallback,
        tipo_id: ht.tipo_id ?? skillConfig.default_tipo_id ?? null,
        tipo1_id: ht.tipo1_id ?? skillConfig.default_tipo1_id ?? null,
        tipo2_id: ht.tipo2_id ?? skillConfig.default_tipo2_id ?? null,
        tipo3_id: ht.tipo3_id ?? skillConfig.default_tipo3_id ?? null,
      }),
    });
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.35 }}>
          {formCopy("introTitle", "agentsListPage.complaintsIntroTitle")}
        </Typography>
        <Typography variant="body2">{formCopy("introHint", "agentsListPage.complaintsIntroHint")}</Typography>
      </Alert>

      <TextField
        label={formCopy("introMessage", "agentsListPage.complaintsIntroMessage")}
        helperText={formCopy("introMessageHelper", "agentsListPage.complaintsIntroMessageHelper")}
        value={skillConfig.intro_message || ""}
        onChange={(e) => mergeComplaintsConfig({ intro_message: e.target.value })}
        disabled={readOnly}
        fullWidth
        multiline
        minRows={2}
      />
      <TextField
        label={formCopy("listeningPrompt", "agentsListPage.complaintsListeningPrompt")}
        helperText={formCopy(
          "listeningPromptHelper",
          "agentsListPage.complaintsListeningPromptHelper"
        )}
        value={skillConfig.listening_prompt || ""}
        onChange={(e) => mergeComplaintsConfig({ listening_prompt: e.target.value })}
        disabled={readOnly}
        fullWidth
        multiline
        minRows={2}
      />
      <TextField
        label={formCopy("followUpPrompt", "agentsListPage.complaintsFollowUpPrompt")}
        helperText={formCopy(
          "followUpPromptHelper",
          "agentsListPage.complaintsFollowUpPromptHelper"
        )}
        value={skillConfig.follow_up_prompt || ""}
        onChange={(e) => mergeComplaintsConfig({ follow_up_prompt: e.target.value })}
        disabled={readOnly}
        fullWidth
        multiline
        minRows={2}
      />
      <TextField
        label={formCopy("transferMessage", "agentsListPage.complaintsTransferMessage")}
        helperText={formCopy(
          "transferMessageHelper",
          "agentsListPage.complaintsTransferMessageHelper"
        )}
        value={skillConfig.transfer_message || ""}
        onChange={(e) => mergeComplaintsConfig({ transfer_message: e.target.value })}
        disabled={readOnly}
        fullWidth
        multiline
        minRows={2}
      />
      <LeadTypeCascadeSelect
        value={leadTypesFromComplaintsDefaults(skillConfig)}
        onChange={(types) => mergeComplaintsConfig(complaintsDefaultsPatchFromLeadTypes(types))}
        disabled={readOnly}
        tipoLabel={formCopy("defaultTipoId", "agentsListPage.complaintsDefaultTipoId")}
        helperText={formCopy("defaultTipoIdHelper", "agentsListPage.complaintsDefaultTipoIdHelper")}
      />
      <TextField
        label={formCopy("minFragments", "agentsListPage.complaintsMinFragments")}
        helperText={formCopy("minFragmentsHelper", "agentsListPage.complaintsMinFragmentsHelper")}
        type="number"
        inputProps={{ min: 1, max: 10 }}
        value={skillConfig.min_fragments_before_handoff ?? 2}
        onChange={(e) =>
          mergeComplaintsConfig({ min_fragments_before_handoff: Number(e.target.value) || 2 })
        }
        disabled={readOnly}
        fullWidth
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, pt: 1 }}>
        {formCopy("handoffTitle", "agentsListPage.complaintsHandoffTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
        {formCopy("handoffSectionHint", "agentsListPage.complaintsHandoffSectionHint")}
      </Typography>
      <AssigneeTransferField
        label={formCopy("handoffTarget", "agentsListPage.complaintsHandoffTarget")}
        helperText={formCopy("handoffTargetHelper", "agentsListPage.complaintsHandoffTargetHelper")}
        selection={handoffSelection}
        onApply={applyHandoffSelection}
        disabled={readOnly || loadingAgents || loadingUsers}
        loading={loadingAgents || loadingUsers}
        agents={handoffAgents}
        users={users}
        includeQueue
        queueLabel={queueLabel}
        shortcutsGroup={t("agentsListPage.lifecycleTransferShortcutsGroup", {
          defaultValue: "Fila e atalhos",
        })}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={!disableLoggedUsersOnly && loggedUsersOnly}
            disabled={readOnly || disableLoggedUsersOnly}
              onChange={(e) =>
              mergeComplaintsConfig({
                handoff_target: applyLoggedUsersOnlyFlag(skillConfig.handoff_target || {}, e.target.checked),
              })
            }
          />
        }
        label={
          <Box>
            <Typography variant="body2">
              {t("agentsListPage.lifecycleHandoffLoggedUsersOnly")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("agentsListPage.lifecycleHandoffLoggedUsersOnlyHelper")}
            </Typography>
          </Box>
        }
      />
      {!disableLoggedUsersOnly && loggedUsersOnly ? (
        <LoggedUsersFallbackField
          fallback={skillConfig.handoff_target?.logged_users_fallback}
          users={users}
          agents={handoffAgents}
          disabled={readOnly}
          onChange={(nextFallback) =>
            mergeComplaintsConfig({
              handoff_target: {
                ...(skillConfig.handoff_target || {}),
                logged_users_only: true,
                logged_users_fallback: nextFallback,
              },
            })
          }
        />
      ) : null}
    </Stack>
  );
}
